// src/state/chat/chatState.js

import { chatService } from '../../services/chat/chatService';

class ChatState {
  constructor() {
    this._chatService = chatService;
    this._messages = [];
    this._conversations = [];
    this._activeConversationId = null;

    // Stream controllers
    this._messagesController = null;
    this._conversationsController = null;
    this._loadingController = null;
    this._errorController = null;

    // Subscriptions
    this._messageSubscription = null;
    this._errorSubscription = null;
    this._connectionSubscription = null;
    this._conversationsSubscription = null;

    // Event listeners
    this._messageListeners = [];
    this._conversationListeners = [];
    this._loadingListeners = [];
    this._errorListeners = [];
  }

  static getInstance() {
    if (!ChatState._instance) {
      ChatState._instance = new ChatState();
    }
    return ChatState._instance;
  }

  // ── Getters ────────────────────────────────────────────────────────────────

  get messages() {
    return [...this._messages];
  }

  get conversations() {
    return [...this._conversations];
  }

  get activeConversationId() {
    return this._activeConversationId;
  }

  // ── Stream listeners ──────────────────────────────────────────────────────

  addMessageListener(callback) {
    if (typeof callback === 'function') {
      this._messageListeners.push(callback);
    }
  }

  removeMessageListener(callback) {
    const index = this._messageListeners.indexOf(callback);
    if (index !== -1) {
      this._messageListeners.splice(index, 1);
    }
  }

  addConversationListener(callback) {
    if (typeof callback === 'function') {
      this._conversationListeners.push(callback);
    }
  }

  removeConversationListener(callback) {
    const index = this._conversationListeners.indexOf(callback);
    if (index !== -1) {
      this._conversationListeners.splice(index, 1);
    }
  }

  addLoadingListener(callback) {
    if (typeof callback === 'function') {
      this._loadingListeners.push(callback);
    }
  }

  removeLoadingListener(callback) {
    const index = this._loadingListeners.indexOf(callback);
    if (index !== -1) {
      this._loadingListeners.splice(index, 1);
    }
  }

  addErrorListener(callback) {
    if (typeof callback === 'function') {
      this._errorListeners.push(callback);
    }
  }

  removeErrorListener(callback) {
    const index = this._errorListeners.indexOf(callback);
    if (index !== -1) {
      this._errorListeners.splice(index, 1);
    }
  }

  // ── Internal notification helpers ────────────────────────────────────────

  _notifyMessages() {
    const messages = this.messages;
    for (const listener of this._messageListeners) {
      try {
        listener(messages);
      } catch (error) {
        console.error('Message listener error:', error);
      }
    }
  }

  _notifyConversations() {
    const conversations = this.conversations;
    for (const listener of this._conversationListeners) {
      try {
        listener(conversations);
      } catch (error) {
        console.error('Conversation listener error:', error);
      }
    }
  }

  _notifyLoading(isLoading) {
    for (const listener of this._loadingListeners) {
      try {
        listener(isLoading);
      } catch (error) {
        console.error('Loading listener error:', error);
      }
    }
  }

  _notifyError(error) {
    for (const listener of this._errorListeners) {
      try {
        listener(error);
      } catch (error) {
        console.error('Error listener error:', error);
      }
    }
  }

  // ── INITIALIZE ─────────────────────────────────────────────────────────────

  async initialize() {
    this._notifyLoading(true);

    try {
      // Set up message listener
      this._messageSubscription = this._chatService.messageStream?.addEventListener('message', (event) => {
        this._handleIncomingMessage(event.data);
      });

      // Set up error listener
      this._errorSubscription = this._chatService.errorStream?.addEventListener('message', (event) => {
        this._handleError(event.data);
      });

      // Set up connection listener
      this._connectionSubscription = this._chatService.connectionStream?.addEventListener('message', (event) => {
        const isConnected = event.data;
        if (!isConnected) {
          this._handleError('Connection lost. Attempting reconnect...');
        }
      });

      // Set up conversations listener
      this._conversationsSubscription = this._chatService.conversationsStream?.addEventListener('message', (event) => {
        const convs = event.data || [];
        this._conversations = convs;
        this._notifyConversations();

        if (this._activeConversationId) {
          this._updateActiveConversationMessages();
        }
      });

      // Load initial messages
      await this._chatService.fetchMessages();

      this._notifyLoading(false);
    } catch (error) {
      this._notifyLoading(false);
      this._handleError(`Failed to initialize chat: ${error}`);
    }
  }

  // ── LOAD A CONVERSATION ───────────────────────────────────────────────────

  async loadConversation(conversationId) {
    this._activeConversationId = conversationId;
    this._updateActiveConversationMessages();
  }

  // ── UPDATE ACTIVE CONVERSATION MESSAGES ──────────────────────────────────

  _updateActiveConversationMessages() {
    if (!this._activeConversationId) return;

    const messages = this._chatService.getConversationMessages(
      this._activeConversationId
    );

    this._messages = messages;
    this._notifyMessages();
  }

  // ── HANDLE INCOMING MESSAGE ──────────────────────────────────────────────

  _handleIncomingMessage(message) {
    if (message.conversationId === this._activeConversationId) {
      const exists = this._messages.some(m => m.id === message.id);
      if (!exists) {
        this._messages.push(message);
        this._messages.sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());
        this._notifyMessages();
      }
    }
  }

  // ── SEND MESSAGE ──────────────────────────────────────────────────────────

  async sendMessage(message) {
    try {
      await this._chatService.sendMessage(message);

      if (message.conversationId === this._activeConversationId) {
        const exists = this._messages.some(m => m.id === message.id);
        if (!exists) {
          this._messages.push(message);
          this._messages.sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());
          this._notifyMessages();
        }
      }
    } catch (error) {
      this._handleError(`Failed to send message: ${error}`);
    }
  }

  // ── ERROR HANDLING ────────────────────────────────────────────────────────

  _handleError(error) {
    this._notifyError(error);
  }

  // ── CLEAR MESSAGES ────────────────────────────────────────────────────────

  clearMessages() {
    this._messages = [];
    this._notifyMessages();
  }

  // ── SET ACTIVE CONVERSATION ──────────────────────────────────────────────

  setActiveConversation(conversationId) {
    this._activeConversationId = conversationId;
    this._updateActiveConversationMessages();
  }

  // ── GET MESSAGES FOR CONVERSATION ────────────────────────────────────────

  getMessagesForConversation(conversationId) {
    return this._chatService.getConversationMessages(conversationId);
  }

  // ── MARK CONVERSATION AS READ ────────────────────────────────────────────

  async markConversationAsRead(conversationId) {
    try {
      await this._chatService.markConversationAsRead(conversationId);
      
      // Update local unread count
      const conv = this._conversations.find(c => c.id === conversationId);
      if (conv) {
        conv.unreadCount = 0;
        this._notifyConversations();
      }
    } catch (error) {
      console.error('markConversationAsRead error:', error);
    }
  }

  // ── DISCONNECT ────────────────────────────────────────────────────────────

  async disconnect() {
    await this._chatService.disconnect();
  }

  // ── DISPOSE ───────────────────────────────────────────────────────────────

  dispose() {
    if (this._messageSubscription) {
      this._messageSubscription.close();
      this._messageSubscription = null;
    }
    if (this._errorSubscription) {
      this._errorSubscription.close();
      this._errorSubscription = null;
    }
    if (this._connectionSubscription) {
      this._connectionSubscription.close();
      this._connectionSubscription = null;
    }
    if (this._conversationsSubscription) {
      this._conversationsSubscription.close();
      this._conversationsSubscription = null;
    }

    this._messageListeners = [];
    this._conversationListeners = [];
    this._loadingListeners = [];
    this._errorListeners = [];
    this._messages = [];
    this._conversations = [];
    this._activeConversationId = null;
  }
}

// ── Singleton export ─────────────────────────────────────────────────────────

const chatState = ChatState.getInstance();
export { chatState };
export default chatState;